/**
 * Feature Error Boundary
 *
 * Feature-level error boundary that catches errors in specific feature modules
 *
 * @module components/errors/FeatureErrorBoundary
 */

import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { logError } from '../../lib/logging/errorLogger';
import { addBreadcrumb } from '../../lib/logging/sentry';
import { ErrorSeverity } from '../../lib/errors/AppError';
import { Button } from '../ui/button';
import { ErrorFallback } from './ErrorFallback';
import { LanguageContext } from '@/context/LanguageContext';

interface Props {
  children: ReactNode;
  featureName: string;
  fallbackMessage?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Feature-level Error Boundary
 *
 * Catches rendering errors within a feature module and displays
 * a fallback UI with option to retry or navigate back
 */
export class FeatureErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Add breadcrumb to track the feature error
    addBreadcrumb(`Error in feature: ${this.props.featureName}`, 'error', {
      feature: this.props.featureName,
      errorMessage: error.message,
      errorName: error.name,
    });

    // Log the error with feature context
    logError(error, {
      component: 'FeatureErrorBoundary',
      feature: this.props.featureName,
      extra: {
        componentStack: errorInfo.componentStack,
        severity: ErrorSeverity.ERROR,
      },
    });
  }

  handleRetry = (): void => {
    // Reset error state to retry rendering
    this.setState({
      hasError: false,
      error: null,
    });
  };

  handleGoBack = (): void => {
    // Navigate back in history
    window.history.back();
  };

  handleGoHome = (): void => {
    // Navigate to home page
    window.location.href = '/';
  };

  render(): ReactNode {
    if (this.state.hasError) {
      const { featureName, fallbackMessage } = this.props;

      return (
        <LanguageContext.Consumer>
          {(ctx) => {
            const t = ctx?.t ?? ((key: string) => key);
            return (
              <ErrorFallback
                title={t('errors.boundary.title')}
                description={
                  fallbackMessage ||
                  t('errors.boundary.featureDescription', { feature: featureName })
                }
                details={
                  import.meta.env.DEV && this.state.error
                    ? {
                        heading: t('errors.boundary.errorDetails'),
                        message: this.state.error.message,
                        trace: this.state.error.stack,
                        traceLabel: t('errors.boundary.stackTrace'),
                      }
                    : undefined
                }
                actions={
                  <>
                    <Button onClick={this.handleRetry}>{t('errors.boundary.tryAgain')}</Button>
                    <Button variant="outline" onClick={this.handleGoBack}>
                      {t('errors.boundary.goBack')}
                    </Button>
                    <Button variant="ghost" onClick={this.handleGoHome}>
                      {t('errors.boundary.goHome')}
                    </Button>
                  </>
                }
              />
            );
          }}
        </LanguageContext.Consumer>
      );
    }

    return this.props.children;
  }
}
